import { useMemo } from 'react';
import { TrendingUp, TrendingDown, Minus, Globe, MapPin, Building, Users } from 'lucide-react';
import {
  currentCompetitorPosition,
  hasManualPositioning,
  isCompetitorResearchStale,
  type CompetitorAIInsights,
} from '../utils/competitorResearch';

interface CompetitorData {
  id: string;
  name: string;
  priceQuality: 'low' | 'medium' | 'high' | 'unknown';
  geoScope: 'local' | 'regional' | 'national' | 'international' | 'unknown';
  marketSize: number | null;
  strategy?: string;
  capabilities?: string;
  aiInsights?: CompetitorAIInsights | null;
}

interface CompetitiveAnalysisChartProps {
  competitors: CompetitorData[];
  className?: string;
}

export default function CompetitiveAnalysisChart({
  competitors,
  className = '',
}: CompetitiveAnalysisChartProps) {
  const chartData = useMemo(() => {
    const priceQualityMap = { low: 1, medium: 2, high: 3 };
    const geoScopeMap = { local: 1, regional: 2, national: 3, international: 4 };

    // Initial positioning
    const initialData = competitors.flatMap((competitor) => {
      const position = currentCompetitorPosition(competitor);
      return position
        ? [
            {
              ...competitor,
              ...position,
              x: geoScopeMap[position.geoScope],
              y: priceQualityMap[position.priceQuality],
              size: position.marketSize === null ? null : Math.max(position.marketSize, 5),
            },
          ]
        : [];
    });
    const currentMaxMarketSize = Math.max(
      ...initialData.map((competitor) => competitor.marketSize ?? 0),
      10
    );

    // Collision detection and adjustment
    const adjustedData = [...initialData];
    const maxIterations = 50;
    const repulsionForce = 0.3;

    for (let iteration = 0; iteration < maxIterations; iteration++) {
      let moved = false;

      for (let i = 0; i < adjustedData.length; i++) {
        for (let j = i + 1; j < adjustedData.length; j++) {
          const a = adjustedData[i];
          const b = adjustedData[j];

          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          // Calculate minimum distance based on bubble sizes
          const aRadius =
            Math.max(
              24,
              Math.min(a.size === null ? 24 : (a.size / currentMaxMarketSize) * 80, 80)
            ) / 100;
          const bRadius =
            Math.max(
              24,
              Math.min(b.size === null ? 24 : (b.size / currentMaxMarketSize) * 80, 80)
            ) / 100;
          const minDistance = (aRadius + bRadius) * 0.8; // Allow slight overlap for visual appeal

          if (distance < minDistance && distance > 0) {
            // Calculate repulsion force
            const force = (minDistance - distance) * repulsionForce;
            const forceX = (dx / distance) * force;
            const forceY = (dy / distance) * force;

            // Apply force with bounds checking
            a.x = Math.max(0.8, Math.min(4.2, a.x + forceX));
            a.y = Math.max(0.8, Math.min(3.2, a.y + forceY));
            b.x = Math.max(0.8, Math.min(4.2, b.x - forceX));
            b.y = Math.max(0.8, Math.min(3.2, b.y - forceY));

            moved = true;
          }
        }
      }

      if (!moved) break; // Converged
    }

    return adjustedData;
  }, [competitors]);

  const maxMarketSize = Math.max(...chartData.map((competitor) => competitor.marketSize ?? 0), 10);
  const unpositionedCount = competitors.filter(
    (competitor) => competitor.priceQuality === 'unknown' || competitor.geoScope === 'unknown'
  ).length;
  const stalePositioningCount = competitors.length - chartData.length - unpositionedCount;

  const getPriceQualityIcon = (level: CompetitorData['priceQuality']) => {
    switch (level) {
      case 'low':
        return <TrendingDown className="h-4 w-4 text-red-500" />;
      case 'medium':
        return <Minus className="h-4 w-4 text-yellow-500" />;
      case 'high':
        return <TrendingUp className="h-4 w-4 text-green-500" />;
    }
  };

  const getGeoScopeIcon = (scope: CompetitorData['geoScope']) => {
    switch (scope) {
      case 'local':
        return <MapPin className="h-4 w-4 text-blue-500" />;
      case 'regional':
        return <Building className="h-4 w-4 text-purple-500" />;
      case 'national':
        return <Users className="h-4 w-4 text-orange-500" />;
      case 'international':
        return <Globe className="h-4 w-4 text-green-500" />;
    }
  };

  const getCompetitorColor = (priceQuality: string, geoScope: string) => {
    // Color based on competitive position
    if (priceQuality === 'high' && (geoScope === 'national' || geoScope === 'international')) {
      return 'bg-red-500'; // High threat
    } else if (priceQuality === 'medium' && geoScope !== 'local') {
      return 'bg-yellow-500'; // Medium threat
    } else {
      return 'bg-blue-500'; // Lower threat
    }
  };

  if (competitors.length === 0) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-8 ${className}`}>
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <TrendingUp className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Competitive Data</h3>
          <p className="text-gray-500">Add competitors to see competitive analysis visualization</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl border border-gray-200 shadow-sm p-6 ${className}`}>
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Competitive Positioning Matrix</h3>
        <p className="text-sm text-gray-600">
          Position shows price/quality vs geographic reach. Market share determines bubble size when
          known; fixed small markers indicate unknown share.
        </p>
        {unpositionedCount > 0 && (
          <p className="mt-2 text-sm text-gray-500">
            {unpositionedCount} {unpositionedCount === 1 ? 'competitor needs' : 'competitors need'}{' '}
            a price/quality or geographic assessment before appearing on this chart.
          </p>
        )}
        {stalePositioningCount > 0 && (
          <p className="mt-2 text-sm text-amber-700">
            {stalePositioningCount}{' '}
            {stalePositioningCount === 1 ? 'competitor has' : 'competitors have'} historical AI
            positioning that needs reassessment and is excluded from this chart. Saved values and
            research evidence remain available for review.
          </p>
        )}
      </div>

      {/* Chart Container with proper margins */}
      <div className="mx-8 my-8">
        {/* Main Chart Area with Y-Axis */}
        <div className="flex">
          {/* Y-Axis Labels */}
          <div className="w-24 flex flex-col justify-between h-80 pr-4">
            <div className="flex items-center justify-end space-x-2">
              <span className="text-sm font-medium text-gray-700">High</span>
              <TrendingUp className="h-4 w-4 text-green-500" />
            </div>
            <div className="flex items-center justify-end space-x-2">
              <span className="text-sm font-medium text-gray-700">Medium</span>
              <Minus className="h-4 w-4 text-yellow-500" />
            </div>
            <div className="flex items-center justify-end space-x-2">
              <span className="text-sm font-medium text-gray-700">Low</span>
              <TrendingDown className="h-4 w-4 text-red-500" />
            </div>
          </div>

          {/* Chart Area */}
          <div className="flex-1">
            <div className="relative h-80 border-2 border-gray-300 rounded-lg bg-gradient-to-br from-gray-50 to-gray-100">
              {/* Grid Lines */}
              <div className="absolute inset-1 grid grid-cols-4 grid-rows-3 gap-0">
                {Array.from({ length: 12 }).map((_, i) => (
                  <div key={i} className="border-r border-b border-gray-300 border-opacity-40" />
                ))}
              </div>

              {/* Competitor Bubbles */}
              {chartData.map((competitor) => {
                const x = ((competitor.x - 0.5) / 4) * 95 + 2.5; // Convert to percentage with padding
                const y = ((3.5 - competitor.y) / 3) * 95 + 2.5; // Invert Y axis and convert to percentage with padding
                const size =
                  competitor.size === null
                    ? 24
                    : Math.max(24, Math.min((competitor.size / maxMarketSize) * 80, 80)); // Unknown share has a fixed marker, never an estimated value.

                return (
                  <div
                    key={competitor.id}
                    role="img"
                    aria-label={`${competitor.name}: ${competitor.priceQuality} price/quality, ${competitor.geoScope} scope, ${competitor.marketSize === null ? 'market share unknown' : `${competitor.marketSize}% market share`}`}
                    className="absolute transform -translate-x-1/2 -translate-y-1/2 group cursor-pointer z-10"
                    style={{
                      left: `${x}%`,
                      top: `${y}%`,
                      width: `${size}px`,
                      height: `${size}px`,
                    }}
                  >
                    <div
                      className={`w-full h-full rounded-full ${getCompetitorColor(competitor.priceQuality, competitor.geoScope)} opacity-80 hover:opacity-100 transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-xl`}
                    >
                      <span className="text-white font-bold text-xs text-center leading-tight">
                        {competitor.name
                          .split(' ')
                          .map((word) => word[0])
                          .join('')
                          .substring(0, 2)}
                      </span>
                    </div>

                    {/* Enhanced Tooltip */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:block z-20">
                      <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl min-w-max">
                        <div className="font-semibold text-white">{competitor.name}</div>
                        {isCompetitorResearchStale(competitor) &&
                          hasManualPositioning(competitor, 'priceQuality') &&
                          hasManualPositioning(competitor, 'geoScope') && (
                            <div className="mt-1 text-blue-200">
                              Positioning entered manually · earlier AI research needs reassessment
                            </div>
                          )}
                        <div className="flex items-center space-x-1 mt-1 text-gray-200">
                          {getPriceQualityIcon(competitor.priceQuality)}
                          <span className="capitalize">
                            {competitor.priceQuality} Price/Quality
                          </span>
                        </div>
                        <div className="flex items-center space-x-1 mt-1 text-gray-200">
                          {getGeoScopeIcon(competitor.geoScope)}
                          <span className="capitalize">{competitor.geoScope} Scope</span>
                        </div>
                        <div className="mt-1 text-blue-200">
                          Market Share:{' '}
                          {competitor.marketSize === null ? 'Unknown' : `${competitor.marketSize}%`}
                        </div>
                        {competitor.strategy && !isCompetitorResearchStale(competitor) && (
                          <div className="mt-1 max-w-48 text-gray-300 text-xs leading-relaxed">
                            {competitor.strategy.length > 60
                              ? `${competitor.strategy.substring(0, 60)}...`
                              : competitor.strategy}
                          </div>
                        )}
                      </div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                );
              })}

              {/* Subtle Quadrant Background Colors */}
              <div className="absolute inset-1 grid grid-cols-2 grid-rows-2 gap-0 pointer-events-none">
                <div className="bg-yellow-50 opacity-30"></div>
                <div className="bg-red-50 opacity-30"></div>
                <div className="bg-green-50 opacity-30"></div>
                <div className="bg-orange-50 opacity-30"></div>
              </div>
            </div>

            {/* X-Axis Labels */}
            <div className="flex justify-between mt-4 px-4">
              <div className="flex flex-col items-center space-y-1">
                <MapPin className="h-4 w-4 text-blue-500" />
                <span className="text-xs font-medium text-gray-700">Local</span>
              </div>
              <div className="flex flex-col items-center space-y-1">
                <Building className="h-4 w-4 text-purple-500" />
                <span className="text-xs font-medium text-gray-700">Regional</span>
              </div>
              <div className="flex flex-col items-center space-y-1">
                <Users className="h-4 w-4 text-orange-500" />
                <span className="text-xs font-medium text-gray-700">National</span>
              </div>
              <div className="flex flex-col items-center space-y-1">
                <Globe className="h-4 w-4 text-green-500" />
                <span className="text-xs font-medium text-gray-700">International</span>
              </div>
            </div>

            {/* X-Axis Label */}
            <div className="text-center mt-4">
              <span className="text-sm font-semibold text-gray-700">Geographic Scope</span>
            </div>
          </div>
        </div>
      </div>

      {/* Clean Legend */}
      <div className="mt-8 bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-4 text-center">Chart Legend</h4>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 text-sm">
          <div className="text-center">
            <h5 className="font-medium text-gray-800 mb-3">Competitive Threat</h5>
            <div className="space-y-2">
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 rounded-full bg-red-500"></div>
                <span className="text-gray-700">High Threat</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
                <span className="text-gray-700">Medium Threat</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 rounded-full bg-blue-500"></div>
                <span className="text-gray-700">Lower Threat</span>
              </div>
            </div>
          </div>

          <div className="text-center">
            <h5 className="font-medium text-gray-800 mb-3">Price/Quality Level</h5>
            <div className="space-y-2">
              <div className="flex items-center justify-center space-x-2">
                <TrendingUp className="h-4 w-4 text-green-500" />
                <span className="text-gray-700">High</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <Minus className="h-4 w-4 text-yellow-500" />
                <span className="text-gray-700">Medium</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <TrendingDown className="h-4 w-4 text-red-500" />
                <span className="text-gray-700">Low</span>
              </div>
            </div>
          </div>

          <div className="text-center">
            <h5 className="font-medium text-gray-800 mb-3">Geographic Scope</h5>
            <div className="space-y-2">
              <div className="flex items-center justify-center space-x-2">
                <Globe className="h-4 w-4 text-green-500" />
                <span className="text-gray-700">International</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <Users className="h-4 w-4 text-orange-500" />
                <span className="text-gray-700">National</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <Building className="h-4 w-4 text-purple-500" />
                <span className="text-gray-700">Regional</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <MapPin className="h-4 w-4 text-blue-500" />
                <span className="text-gray-700">Local</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 text-center">
          <p className="text-xs text-gray-600">
            Bubble size represents recorded market share. Fixed small markers have unknown share.
            Hover for details.
          </p>
        </div>
      </div>
    </div>
  );
}
